import { DomainError } from '../errors/domain-error';
import { normalizeAudienceEmail } from '../audience/audience-crm';

export const MARKETING_CSV_IMPORT_MAX_ROWS = 1000;

export type MarketingCsvImportRow = {
  email: string;
  name?: string;
  tags?: string[];
  line: number;
};

export type MarketingCsvParseResult = {
  rows: MarketingCsvImportRow[];
  skippedEmpty: number;
  headerMap: Record<string, number>;
};

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

/** Parse LOAMIFY marketing CSV (email required; optional name/tags). */
export function parseMarketingSubscriberCsv(csvText: string): MarketingCsvParseResult {
  const lines = csvText
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line, index, arr) => !(index === arr.length - 1 && line === ''));

  if (lines.length === 0) {
    throw new DomainError( 'CSV is empty');
  }

  const headerCells = splitCsvLine(lines[0]!);
  const headerMap: Record<string, number> = {};
  headerCells.forEach((cell, index) => {
    headerMap[normalizeHeader(cell)] = index;
  });

  const emailIdx =
    headerMap.email ?? headerMap.emailaddress ?? headerMap.subscriberemail ?? null;
  if (emailIdx == null) {
    throw new DomainError( 'CSV must include an email column');
  }
  const nameIdx = headerMap.name ?? headerMap.fullname ?? headerMap.displayname ?? null;
  const tagsIdx = headerMap.tags ?? headerMap.tag ?? null;

  const rows: MarketingCsvImportRow[] = [];
  let skippedEmpty = 0;
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]!);
    if (cells.every((cell) => !cell.trim())) {
      skippedEmpty += 1;
      continue;
    }
    const rawEmail = cells[emailIdx]?.trim() ?? '';
    if (!rawEmail || !rawEmail.includes('@')) {
      throw new DomainError( `Invalid email on line ${i + 1}`);
    }
    const email = normalizeAudienceEmail(rawEmail);
    const name = nameIdx != null ? cells[nameIdx]?.trim() || undefined : undefined;
    const tagsRaw = tagsIdx != null ? cells[tagsIdx]?.trim() || '' : '';
    const tags = tagsRaw
      ? tagsRaw
          .split(/[|;,]/)
          .map((tag) => tag.trim())
          .filter(Boolean)
          .slice(0, 20)
      : undefined;
    rows.push({ email, name, tags, line: i + 1 });
  }

  if (rows.length === 0) {
    throw new DomainError( 'CSV has no subscriber rows');
  }
  if (rows.length > MARKETING_CSV_IMPORT_MAX_ROWS) {
    throw new DomainError(
      `CSV exceeds ${MARKETING_CSV_IMPORT_MAX_ROWS} rows (got ${rows.length})`,
    );
  }

  return { rows, skippedEmpty, headerMap };
}

export function assertMarketingImportAttestation(attested: boolean) {
  if (!attested) {
    throw new DomainError(
      'Permission attestation is required before importing subscribers',
    );
  }
}

export function marketingSubscriberExportHeaders() {
  return [
    'email',
    'name',
    'status',
    'publicationId',
    'publicationSlug',
    'consentPurpose',
    'consentTextVersion',
    'consentSource',
    'consentAt',
    'tags',
    'contactId',
  ] as const;
}
